import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createConsoleClient } from "./api/client";
import { ApiProvider, type ConsoleApi } from "./api/context";
import { createSessionStore } from "./api/session";
import { App } from "./App";
import { isConsoleLanguage, setLanguage } from "./i18n";
import { clearSessionLanguage, readSessionLanguage } from "./i18n/session-language";
import "./styles/prototype.css";
import "./styles/console.css";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:4000";

async function createApi(): Promise<ConsoleApi> {
  // Dev-only fixture mode (`?fixture=aminata`): answers the console endpoints
  // from local data, with a memory-only session. `import.meta.env.DEV` is
  // `false` in a production build, so this branch and the fixtures are
  // compiled out of it.
  if (import.meta.env.DEV) {
    const params = new URLSearchParams(window.location.search);
    const name = params.get("fixture");
    if (name) {
      // `&lang=ar` sets the persona's consoleLanguage, and wins over a pick
      // left in this tab's session by an earlier visit.
      const lang = params.get("lang");
      if (isConsoleLanguage(lang)) clearSessionLanguage();
      const { createFixtureApi } = await import("./fixtures/fixture-api");
      const api = createFixtureApi(name, isConsoleLanguage(lang) ? lang : undefined);
      if (api) return api;
    }
  }
  const session = createSessionStore();
  return { session, client: createConsoleClient({ baseUrl: API_BASE_URL, session }) };
}

// Until the staff member is known (sign-in, set-password): this session's pick, else French.
setLanguage(readSessionLanguage() ?? "fr");

void createApi().then((api) => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <ApiProvider api={api}>
        <App />
      </ApiProvider>
    </StrictMode>,
  );
});
